<template>
    <kgr-form-scaffold
        :with-primary-buttons="false"
        @backBtnClicked="redirectToOverview"
    >
        <div>
            <div class="kgr-row">
                <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
                    <div class="kgr-postfix__fluid">
                        <kgr-view-picker
                            :value="view"
                            @post-input="updateView($event)"
                        />
                    </div>
                    <div class="kgr-postfix__fixed">
                        <kgr-print-button />
                    </div>
                </div>

                <div class="kgr-grid kgr-grid--columnGutters kgr-grid--inlineBlock">
                    <div class="kgr-grid__cell">
                        <kgr-member-status-picker
                            :statuses="statuses"
                            :types="types"
                            @post-input="updateStatuses"
                        />
                    </div>
                </div>
                <div class="kgr-grid kgr-grid--columnGutters kgr-row">
                    <div class="kgr-grid__cell">
                        <label class="kgr-inputGroup">
                            <div
                                v-t="'search'"
                                class="kgr-inputGroup__label"
                            ></div>
                            <input
                                v-model="search"
                                type="text"
                                class="kgr-input"
                            />
                        </label>
                    </div>
                    <div
                        v-if="view === 'grid'"
                        class="kgr-grid__cell animated fadeIn"
                    >
                        <label class="kgr-inputGroup">
                            <div
                                v-t="'sort'"
                                class="kgr-inputGroup__label"
                            ></div>
                            <kgr-sort-select
                                :value="sort"
                                @post-input="updateSort($event)"
                            />
                        </label>
                    </div>
                    <div class="kgr-grid__cell">
                        <div class="kgr-inputGroup__label">
&nbsp;
                        </div>
                    </div>
                </div>
            </div>

            <kgr-members-list
                :members="members"
                :view="view"
                :loading="loading"
                :can-load-more="canLoadMore"
                @loadMore="loadMore"
            />
        </div>
    </kgr-form-scaffold>
</template>

<script>
import DepartmentRepository from '../../js/repository/DepartmentRepository.js';
import KgrFormScaffold from '../../components/common/form/FormScaffold.vue';
import KgrMemberStatusPicker from '../../components/common/form/picker/MemberStatusPicker.vue';
import MembersList from '../../components/members/MembersList.vue';
import PrintButton from '../../components/common/form/button/PrintButton.vue';
import SortSelect from '../../components/members/SortSelect.vue';
import ViewPicker from '../../components/common/form/picker/ViewPicker.vue';

const DEPARTMENT_MEMBERS_OVERVIEW_VIEW = 'kgr:department-members:overview:view';
const DEPARTMENT_MEMBERS_OVERVIEW_SORT = 'kgr:department-members:overview:sort';
const DEPARTMENT_MEMBERS_OVERVIEW_STATUSES = 'kgr:department-members:overview:contract-statuses';
const DEPARTMENT_MEMBERS_OVERVIEW_TYPES = 'kgr:department-members:overview:contract-types';

export default {
    components: {
        'kgr-form-scaffold': KgrFormScaffold,
        'kgr-members-list': MembersList,
        'kgr-member-status-picker': KgrMemberStatusPicker,
        'kgr-print-button': PrintButton,
        'kgr-sort-select': SortSelect,
        'kgr-view-picker': ViewPicker,
    },
    props: {
        overviewUrl: {
            type: String,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        departmentId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            members: null,
            search: '',
            sort: null,
            statuses: [0, 1, 2, 3],
            types: ['regular', 'passive', 'guest'],
            view: 'grid',
            timeout: null,
            limit: 100,
            offset: 0,
            canLoadMore: true,
            pickerReady: false,
        };
    },
    watch: {
        search() {
            clearTimeout(this.timeout);
            if (this.search.trim().length < 3 && this.search.trim().length > 0) {
                return;
            }
            this.timeout = setTimeout(() => {
                if (this.pickerReady) {
                    this.load();
                }
            }, 600);
        },
    },
    created() {
        const view = localStorage.getItem(DEPARTMENT_MEMBERS_OVERVIEW_VIEW);
        if (view !== null && view !== '') {
            this.view = view;
        }
        const statuses = JSON.parse(localStorage.getItem(DEPARTMENT_MEMBERS_OVERVIEW_STATUSES));
        if (statuses !== null) {
            this.statuses = statuses;
        }
        const types = JSON.parse(localStorage.getItem(DEPARTMENT_MEMBERS_OVERVIEW_TYPES));
        if (types !== null) {
            this.types = types;
        }

        this.sort = localStorage.getItem(DEPARTMENT_MEMBERS_OVERVIEW_SORT);
    },
    mounted() {
        this.load();
    },
    methods: {
        updateView(view) {
            this.view = view;
            if (view !== null) {
                localStorage.setItem(DEPARTMENT_MEMBERS_OVERVIEW_VIEW, view);
            } else {
                localStorage.removeItem(DEPARTMENT_MEMBERS_OVERVIEW_VIEW);
            }
        },
        updateSort(sort) {
            this.sort = sort;

            if (sort !== null) {
                localStorage.setItem(DEPARTMENT_MEMBERS_OVERVIEW_SORT, sort);
            } else {
                localStorage.removeItem(DEPARTMENT_MEMBERS_OVERVIEW_SORT);
            }
            this.load();
        },
        updateStatuses(statusesAndTypes) {
            this.statuses = statusesAndTypes.statuses;
            this.types = statusesAndTypes.types;

            if (this.statuses !== null) {
                localStorage.setItem(DEPARTMENT_MEMBERS_OVERVIEW_STATUSES, JSON.stringify(this.statuses));
            } else {
                localStorage.removeItem(DEPARTMENT_MEMBERS_OVERVIEW_STATUSES);
            }

            if (this.types !== null) {
                localStorage.setItem(DEPARTMENT_MEMBERS_OVERVIEW_TYPES, JSON.stringify(this.types));
            } else {
                localStorage.removeItem(DEPARTMENT_MEMBERS_OVERVIEW_TYPES);
            }

            this.load();
        },
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        load() {
            this.loading = true;
            this.members = [];
            DepartmentRepository.findMembers(this.departmentId, this.statuses.join(','), this.types.join(','), this.search, this.sort, this.limit, this.offset)
                .then((members) => this.members = members)
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                    this.pickerReady = true;
                });
        },
        loadMore() {
            this.offset += this.limit;
            this.loading = true;
            DepartmentRepository.findMembers(this.departmentId, this.statuses.join(','), this.types.join(','), this.search, this.sort, this.limit, this.offset)
                .then((members) => {
                    this.canLoadMore = members.length > 0;
                    this.members = this.members.concat(members);
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
